<template>
  <div>

    <!-- Sección de daños (condicional) -->
    <v-slide-y-transition>
      <v-card v-if="vehicleCondition === 'damages' || vehicleCondition === 'additional'" class="mb-4 damage-details-card" outlined>
        <v-card-title class="subtitle-1">
          <v-icon v-if="vehicleCondition === 'damages'" left color="error">mdi-alert-circle</v-icon>
          <v-icon v-else left color="primary">mdi-camera-plus</v-icon>
          {{ vehicleCondition === 'damages' ? 'Detalles de los daños' : 'Imágenes adicionales' }}
        </v-card-title>

        <v-card 
          v-if="deliveryObservations" 
          class="mb-4 pa-4 info-card-green mx-4" 
          elevation="3"
        >
          <div class="d-flex align-center mb-2">
            <v-icon color="green darken-2" class="mr-2">mdi-information</v-icon>
            <span class="subtitle-2 font-weight-bold green--text text--darken-2">Entrega movimiento</span>
          </div>
          <div class="body-2" style="white-space: pre-wrap;">{{ deliveryObservations }}</div>
        </v-card>

        <v-card-text>
          <v-textarea
            v-if="vehicleCondition === 'damages'"
            :value="damageNotes"
            label="Descripción de los daños"
            placeholder="Describe los daños encontrados en el vehículo..."
            outlined
            dense
            rows="5"
            auto-grow
            counter="500"
            maxlength="500"
            hint="Los saltos de línea se preservarán"
            persistent-hint
            @input="$emit('update:damageNotes', $event)"
          ></v-textarea>

          <!-- Sección de imágenes existentes (solo en modo edición) -->
          <div v-if="savedImages && savedImages.length > 0" class="mb-4 saved-images-container">
            <div class="subtitle-2 mb-2">
              <v-icon small left color="primary">mdi-image-multiple</v-icon>
              Imágenes guardadas ({{ savedImagesCount }})
            </div>
            <v-row>
              <v-col
                v-for="(image, index) in savedImages"
                :key="`saved-${index}`"
                cols="6"
                sm="4"
                md="3"
              >
                <v-card 
                  class="image-preview-card elevation-2" 
                  :class="{ 'disabled-card': imagesToRemove.includes(image.id || index) }"
                  @click="!imagesToRemove.includes(image.id || index) && openImageDialog(image.url || image)"
                >
                  <v-img
                    :src="image.url || image"
                    height="120"
                    cover
                    class="image-preview"
                  >
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      </v-row>
                    </template>
                    
                    <div class="image-overlay d-flex align-center justify-center">
                      <v-btn 
                        v-if="!imagesToRemove.includes(image.id || index)"
                        icon 
                        color="white" 
                        @click.stop="removeImage(index)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                      <v-btn 
                        v-if="!imagesToRemove.includes(image.id || index)"
                        icon 
                        color="white" 
                        @click.stop="openImageDialog(image.url || image)"
                      >
                        <v-icon>mdi-magnify-plus</v-icon>
                      </v-btn>
                    </div>
                  </v-img>
                  
                  <v-chip 
                    v-if="imagesToRemove.includes(image.id)"
                    class="image-badge"
                    x-small
                    color="error"
                  >
                    Eliminada
                  </v-chip>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Sección de nuevas imágenes -->
          <div v-if="newImagePreviews && newImagePreviews.length > 0" class="mb-4 new-images-container">
            <div class="subtitle-2 mb-2">
              <v-icon small left color="success">mdi-image-plus</v-icon>
              Nuevas imágenes ({{ newImagePreviews.length }})
            </div>
            <v-row>
              <v-col
                v-for="(preview, index) in newImagePreviews"
                :key="`new-${index}`"
                cols="6"
                sm="4"
                md="3"
              >
                <v-card class="new-image-preview-card elevation-2" @click="openImageDialog(preview)">
                  <v-img
                    :src="preview"
                    height="120"
                    cover
                    class="image-preview"
                  >
                    <div class="image-overlay d-flex align-center justify-center">
                      <v-btn icon color="white" @click.stop="removeNewImage(index)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                      <v-btn icon color="white" @click.stop="openImageDialog(preview)">
                        <v-icon>mdi-magnify-plus</v-icon>
                      </v-btn>
                    </div>
                  </v-img>
                  
                  <v-chip class="image-badge" x-small color="success">
                    Nueva
                  </v-chip>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Input para subir nuevas imágenes -->
          <v-file-input
            v-if="canAddMoreImages"
            v-model="tempDamageImages"
            accept="image/*"
            multiple
            :label="vehicleCondition === 'damages' ? 'Agregar imágenes de daños' : 'Agregar imágenes'"
            prepend-icon="mdi-camera"
            outlined
            dense
            chips
            show-size
            @change="handleImageChange"
            :hint="`Puedes agregar ${remainingImageSlots} imagen(es) más. Máximo 4 imágenes.`"
            persistent-hint
          ></v-file-input>

          <v-alert
            v-if="!canAddMoreImages"
            type="info"
            dense
            class="mt-2"
          >
            <v-icon left small>mdi-information</v-icon>
            Has alcanzado el límite máximo de 4 imágenes.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-slide-y-transition>

    <!-- Diálogo para vista previa de imágenes -->
    <v-dialog v-model="imageDialog" max-width="90vw" max-height="90vh">
      <v-card class="image-dialog-content">
        <v-card-title class="pa-2">
          <v-spacer></v-spacer>
          <v-btn icon @click="imageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-img
            :src="selectedImageUrl"
            contain
            max-height="80vh"
          ></v-img>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'ImageManager',
  props: {
    vehicleCondition: {
      type: String,
      required: true
    },
    damageNotes: {
      type: String,
      default: ''
    },
    deliveryObservations: {
      type: String,
      default: ''
    },
    savedImages: {
      type: Array,
      default: () => []
    },
    newDamageImages: {
      type: Array,
      default: () => []
    },
    newImagePreviews: {
      type: Array,
      default: () => []
    },
    imagesToRemove: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      tempDamageImages: [],
      imageDialog: false,
      selectedImageUrl: ''
    }
  },

  computed: {
    savedImagesCount() {
      return this.savedImages ? this.savedImages.length - this.imagesToRemove.length : 0
    },
    remainingImageSlots() {
      return Math.max(0, 4 - this.savedImagesCount - (this.newDamageImages ? this.newDamageImages.length : 0))
    },
    canAddMoreImages() {
      return this.remainingImageSlots > 0
    }
  },
  methods: {
    async handleImageChange(files) {
      if (!files || files.length === 0) return
      
      // Si hay archivos nuevos seleccionados, agregarlos a los existentes
      const existingFiles = this.newDamageImages || []
      const newFiles = Array.from(files)
      
      // Verificar que no exceda el límite de 4 imágenes totales
      const totalSlots = 4
      const usedSlots = this.savedImagesCount + existingFiles.length
      const availableSlots = totalSlots - usedSlots
      
      // Si no hay slots disponibles, rechazar todo
      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} imágenes`)
        this.tempDamageImages = []
        return
      }
      
      // Si se seleccionaron más imágenes de las permitidas, tomar solo las que caben
      let filesToProcess = newFiles
      let warningMessage = ''
      
      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} imagen(es). Se rechazaron ${rejected} imagen(es) por exceder el límite de ${totalSlots}.`
      }
      
      // Combinar archivos existentes con los nuevos (solo los que caben)
      const updatedFiles = [...existingFiles, ...filesToProcess]
      
      // Generar vistas previas para las nuevas imágenes - esperar a que todas se carguen
      const newPreviews = [...this.newImagePreviews]
      const previewPromises = filesToProcess.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            resolve(e.target.result)
          }
          reader.readAsDataURL(file)
        })
      })
      
      try {
        // Esperar a que todas las vistas previas se generen
        const generatedPreviews = await Promise.all(previewPromises)
        newPreviews.push(...generatedPreviews)
        
        // Emitir todos los eventos después de que las vistas previas estén listas
        this.$emit('update:newImagePreviews', newPreviews)
        this.$emit('update:newDamageImages', updatedFiles)
        this.$emit('images-changed', updatedFiles)
        
        // Mostrar mensaje apropiado
        const totalImages = this.savedImagesCount + updatedFiles.length
        if (warningMessage) {
          this.$toast.warning(warningMessage)
        } else {
          this.$toast.success(`${filesToProcess.length} imagen(es) agregada(s). Total: ${totalImages}/${totalSlots}`)
        }
      } catch (error) {
        console.error('Error al generar vistas previas:', error)
        this.$toast.error('Error al procesar las imágenes')
      }
      
      // Resetear el input temporal para permitir seleccionar más archivos
      this.$nextTick(() => {
        this.tempDamageImages = []
      })
    },
    
    openImageDialog(imageUrl) {
      this.selectedImageUrl = imageUrl
      this.imageDialog = true
    },
    
    removeImage(index) {
      // Marcar para eliminar del servidor
      const image = this.savedImages[index]
      const imageId = image && image.id !== undefined ? image.id : index
      const updatedImagesToRemove = [...this.imagesToRemove, imageId]
      this.$emit('update:imagesToRemove', updatedImagesToRemove)
      this.$emit('image-removed', { type: 'saved', index, imageId })
    },
    
    removeNewImage(index) {
      // Eliminar la vista previa
      const updatedPreviews = [...this.newImagePreviews]
      updatedPreviews.splice(index, 1)
      this.$emit('update:newImagePreviews', updatedPreviews)
      
      // Eliminar el archivo
      const updatedFiles = [...this.newDamageImages]
      updatedFiles.splice(index, 1)
      this.$emit('update:newDamageImages', updatedFiles)
      this.$emit('image-removed', { type: 'new', index })
    },

    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        txt: 'mdi-file-document-outline',
        zip: 'mdi-folder-zip',
        rar: 'mdi-folder-zip'
      }
      return iconMap[extension] || 'mdi-file-outline'
    },

    getFileIconColor(filename) {
      const extension = filename.split('.').pop().toLowerCase()
      const colorMap = {
        pdf: 'red',
        doc: 'blue',
        docx: 'blue',
        xls: 'green',
        xlsx: 'green',
        txt: 'grey',
        zip: 'orange',
        rar: 'orange'
      }
      return colorMap[extension] || 'grey'
    },

    downloadDoc(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'documento'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.image-preview-card, .new-image-preview-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.image-preview {
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview-card:hover .image-overlay,
.new-image-preview-card:hover .image-overlay {
  opacity: 1;
}

.image-preview-card:hover .image-preview,
.new-image-preview-card:hover .image-preview {
  transform: scale(1.05);
}

.image-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
}

/* Custom info cards with borders */
.info-card-blue {
  background-color: #E3F2FD !important;
  border: 2px solid #1976D2 !important;
  border-radius: 8px !important;
}

.info-card-green {
  background-color: #E8F5E9 !important;
  border: 2px solid #4CAF50 !important;
  border-radius: 8px !important;
}

.image-dialog-content {
  max-height: 80vh;
  background-color: #000;
}

.saved-images-container, .new-images-container {
  transition: all 0.3s ease;
}

.disabled-card {
  opacity: 0.5;
  cursor: default !important;
}

.damage-details-card {
  border-radius: 8px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.saved-docs-container, .new-docs-container {
  transition: all 0.3s ease;
}

.disabled-item {
  opacity: 0.5;
}

.new-doc-item {
  background-color: rgba(76, 175, 80, 0.05);
  border-radius: 4px;
  margin-bottom: 4px;
}
</style>
